import { useState } from "react"
import { Upload, Button, message, Alert } from "antd"
import { UploadOutlined, DownloadOutlined } from "@ant-design/icons"
import * as XLSX from "xlsx"
import apiClient from "../../Utils/apiClient"
import TableBox from "../TableBox/TableBox"
import { getStoredPageSize } from "../../Utils/TableUtils/TableUtils"
import PanelLayout from "../../Layout/PanelLayout"
import StatusTag from "../StatusTag/StatusTag"
import "../../Screens/Users/Users.scss"
import "../../Screens/Operations/Operations.scss"

const BulkRegister = () => {
  const page = "Bulk Register"
  const [fileList, setFileList] = useState([])
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [paginationData, setPaginationData] = useState({
    page: 1,
    pageSize: getStoredPageSize(page) || 10,
    total: 0,
  })

  const handleChange = ({ fileList: next }) => {
    setFileList(next.slice(-1))
  }

  const downloadTemplate = () => {
    const workbook = XLSX.utils.book_new()
    const sheet = XLSX.utils.json_to_sheet([
      {
        User: "Jane Doe",
        "Email ID": "jane@company.com",
        "Serial Number": "ABC123456",
      },
    ])
    sheet["!cols"] = [{ wch: 22 }, { wch: 28 }, { wch: 18 }]
    XLSX.utils.book_append_sheet(workbook, sheet, "Users")
    XLSX.writeFile(workbook, "user-bulk-register-template.xlsx")
  }

  const handleUpload = async () => {
    if (!fileList.length) {
      message.warning("Select an Excel file first.")
      return
    }

    const file = fileList[0].originFileObj
    const formData = new FormData()
    formData.append("file", file)

    try {
      setLoading(true)
      const res = await apiClient("users/bulk-register", "POST", formData)

      if (res.success) {
        message.success("Bulk registration completed.")
        setResults(res.results || [])
        setPaginationData({
          page: 1,
          pageSize: getStoredPageSize(page) || 10,
          total: (res.results || []).length,
        })
      } else {
        message.error(res.message || "Bulk registration failed")
      }
    } catch (err) {
      console.error(err)
      message.error("An error occurred while uploading the Excel file.")
    } finally {
      setLoading(false)
    }
  }

  const handleTableChange = (pagination) => {
    const { current, pageSize } = pagination
    setPaginationData((prev) => ({ ...prev, page: current, pageSize }))
  }

  const statusSummary = results.reduce((acc, row) => {
    const key = String(row?.status || "Unknown")
    acc[key] = (acc[key] || 0) + 1
    return acc
  }, {})

  const columns = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) => <span className="ops-name">{email || "—"}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (status) => <StatusTag status={status} />,
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      render: (reason) => reason || <span className="ops-muted">—</span>,
    },
  ]

  return (
    <PanelLayout
      title="Bulk register"
      subtitle="Upload an Excel sheet to create standard users and assign devices by serial number."
      actions={
        <>
          <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
            Download template
          </Button>
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={handleUpload}
            loading={loading}
            disabled={!fileList.length}
          >
            Upload & register
          </Button>
        </>
      }
    >
      <div className="bulk-register">
        <Alert
          type="info"
          showIcon
          message="Excel columns must match exactly"
          description="Use User, Email ID, and Serial Number. Each row creates a standard user and assigns the device with that serial. Existing emails are skipped."
        />

        <Upload.Dragger
          className="bulk-register__upload"
          accept=".xlsx,.xls"
          fileList={fileList}
          onChange={handleChange}
          beforeUpload={() => false}
          maxCount={1}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">Drop an Excel file here, or click to browse</p>
          <p className="bulk-register__hint">.xlsx or .xls · one file</p>
        </Upload.Dragger>

        {results.length > 0 && (
          <div className="ops-kpis">
            <div className="ops-kpi ops-kpi--static">
              <div>
                <span className="ops-kpi-label">Rows processed</span>
                <strong>{results.length}</strong>
              </div>
            </div>
            {Object.entries(statusSummary).map(([status, count]) => (
              <div className="ops-kpi ops-kpi--static" key={status}>
                <div>
                  <span className="ops-kpi-label">{status}</span>
                  <strong>{count}</strong>
                </div>
              </div>
            ))}
          </div>
        )}

        {results.length > 0 && (
          <TableBox
            className="dashboard-inventory-table"
            page={page}
            columns={columns}
            data={results}
            loading={loading}
            pagination={{
              current: paginationData.page,
              pageSize: paginationData.pageSize,
              total: paginationData.total,
              showSizeChanger: true,
            }}
            onChange={handleTableChange}
            title="Registration results"
          />
        )}
      </div>
    </PanelLayout>
  )
}

export default BulkRegister
